/**
 * The PALM AMI004 file name contract: the shape PALM sends us, and how the PHYSICAL_INVENTORY_ID is
 * read out of it. Used by willChange_import_import_PALM, which parses the identifier out of the
 * file the user selects and makes it the import title.
 *
 * The regex and the human-readable convention live together so the message shown when a file name
 * does not conform cannot drift from the pattern that rejected it.
 */

/**
 * The pattern, written the way the ticket and the code talk about it. Placeholder syntax, so it
 * belongs in logs and developer-facing text - never in a message shown to an operator.
 *
 * It describes the usual shape, not everything AMI004_FILE_NAME enforces: the parser checks the
 * start of the name and the `.csv` extension, but not the timestamp, and accepts any tail after the
 * process instance. Do not tighten the parser to match this text - see AMI004_FILE_NAME below.
 */
export const PALM_AMI004_FILE_NAME_CONVENTION =
    '${prefixCode}_AMI004_O_${PHYSICAL_INVENTORY_ID}_${processInstance}_YYYYMMDD-HHMM.csv';

/**
 * The same shape as something an operator can compare their own file against. A concrete example
 * beats a template: `${prefixCode}` means nothing to the person who picked the file.
 */
export const PALM_AMI004_FILE_NAME_EXAMPLE = 'DER_AMI004_O_DER-PI2026_709799_20251219-1028.csv';

// The start of the name is fixed: a 3 or 4 character agency acronym, then `_AMI004_O_`, then the
// PHYSICAL_INVENTORY_ID, which must be followed by at least one more segment (the process
// instance) that starts with something other than an underscore, so neither `PI_.csv` nor
// `PI__.csv` counts as having one. The leading anchor still rejects a hand-renamed file such as
// `backup_DoJ_AMI004_O_PI-1_20260825-1015.csv`, and the name must still end in `.csv`.
//
// Between those, the tail is deliberately left open. PALM added a process instance after the
// identifier (#2260), and files have been seen with further data after the timestamp - e.g.
// `DJJ_AMI004_O_09292026_1188942_20260929-0953_20260929095426.csv` - so anchoring on a trailing
// timestamp (as #1317 did) either swallowed the process instance into the identifier or rejected
// the file outright. The identifier is now simply underscore segment 3, which means it can no
// longer contain underscores of its own - #2260 dropped that requirement.
const AMI004_FILE_NAME = /^[a-z0-9]{3,4}_AMI004_O_([^_]+)_[^_].*\.csv$/i;

/**
 * @param {string} [fileName]
 * @returns {string|undefined} the PHYSICAL_INVENTORY_ID, or undefined if the name does not conform
 */
export function getPhysicalInventoryIdFromFileName(fileName = '') {
    const [, physicalInventoryId] = AMI004_FILE_NAME.exec(fileName) || [];
    return physicalInventoryId;
}
